import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';
import { ApiResponse } from '../shared/types';
import type { PrepaidRedemptionScanPayload } from '../shared/utils/prepaid-redemption-url.util';
import {
  CreatePrepaidWashBookPayload,
  UpdatePrepaidWashBookPayload,
  DEFAULT_PREPAID_WASH_BOOKS_PAGE_LIMIT,
  emptyPrepaidWashBooksPagination,
  PrepaidWashBook,
  PrepaidWashBooksListApiResponse,
  PrepaidPurchaseStatus,
  PrepaidPurchaseRedemption,
  PrepaidPurchaseRedemptionsListApiResponse,
  PrepaidPurchaseRedemptionsListParams,
  PrepaidPurchaseRedemptionsListResult,
  DEFAULT_PREPAID_PURCHASE_REDEMPTIONS_PAGE_LIMIT,
  PrepaidWashBooksListParams,
  PrepaidWashBooksListResult,
  RedeemPrepaidPurchasePayload,
} from '../models/prepaid-wash-book.models';

@Injectable({
  providedIn: 'root',
})
export class PrepaidWashBookService {
  private readonly apiService = inject(ApiService);
  private readonly logger = inject(LoggerService);

  private static readonly LIST_ENDPOINT = 'package/prepaid-wash-books';
  private static readonly PURCHASES_ENDPOINT = 'package/prepaid-wash-books/purchases';

  createPrepaidWashBook(payload: CreatePrepaidWashBookPayload): Observable<ApiResponse<any>> {
    this.logger.debug('Creating prepaid wash book', payload, 'PrepaidWashBookService');
    return this.apiService.post<any>(PrepaidWashBookService.LIST_ENDPOINT, payload).pipe(
      catchError(err => {
        this.logger.error('Failed to create prepaid wash book', err, 'PrepaidWashBookService');
        return throwError(() => err);
      })
    );
  }

  updatePrepaidWashBook(
    prepaidWashBookId: string,
    payload: UpdatePrepaidWashBookPayload
  ): Observable<ApiResponse<any>> {
    const endpoint = `${PrepaidWashBookService.LIST_ENDPOINT}/${prepaidWashBookId}`;
    this.logger.debug(
      'Updating prepaid wash book',
      { prepaidWashBookId, payload },
      'PrepaidWashBookService'
    );
    return this.apiService.patch<any>(endpoint, payload).pipe(
      catchError(err => {
        this.logger.error('Failed to update prepaid wash book', err, 'PrepaidWashBookService');
        return throwError(() => err);
      })
    );
  }

  deletePrepaidWashBook(prepaidWashBookId: string): Observable<ApiResponse<any>> {
    const endpoint = `${PrepaidWashBookService.LIST_ENDPOINT}/${prepaidWashBookId}`;
    this.logger.debug(
      'Deleting prepaid wash book',
      { prepaidWashBookId },
      'PrepaidWashBookService'
    );
    return this.apiService.delete<any>(endpoint).pipe(
      catchError(err => {
        this.logger.error('Failed to delete prepaid wash book', err, 'PrepaidWashBookService');
        return throwError(() => err);
      })
    );
  }

  /**
   * `GET package/prepaid-wash-books/purchases/{prepaidPurchaseId}/status`
   */
  getPrepaidPurchaseStatus(
    prepaidPurchaseId: string
  ): Observable<ApiResponse<PrepaidPurchaseStatus>> {
    const id = prepaidPurchaseId?.trim();
    if (!id) {
      return throwError(() => new Error('prepaidPurchaseId is required'));
    }
    const endpoint = `${PrepaidWashBookService.PURCHASES_ENDPOINT}/${id}/status`;
    this.logger.debug(
      'Fetching prepaid purchase status',
      { prepaidPurchaseId: id },
      'PrepaidWashBookService'
    );
    return this.apiService.get<PrepaidPurchaseStatus>(endpoint).pipe(
      catchError(err => {
        this.logger.error('Failed to fetch prepaid purchase status', err, 'PrepaidWashBookService');
        return throwError(() => err);
      })
    );
  }

  /**
   * `GET package/prepaid-wash-books/purchases/status?redemption_token={token}`
   */
  getPrepaidPurchaseStatusByRedemptionToken(
    redemptionToken: string
  ): Observable<ApiResponse<PrepaidPurchaseStatus>> {
    const value = redemptionToken?.trim();
    if (!value) {
      return throwError(() => new Error('redemption token is required'));
    }
    const endpoint = `${PrepaidWashBookService.PURCHASES_ENDPOINT}/status`;
    this.logger.debug(
      'Fetching prepaid purchase status by redemption token',
      { redemptionToken: value },
      'PrepaidWashBookService'
    );
    return this.apiService.get<PrepaidPurchaseStatus>(endpoint, { redemption_token: value }).pipe(
      catchError(err => {
        this.logger.error(
          'Failed to fetch prepaid purchase status by redemption token',
          err,
          'PrepaidWashBookService'
        );
        return throwError(() => err);
      })
    );
  }

  /**
   * CWS-1908: `GET package/prepaid-wash-books/purchases/status/by-code?redemption_code={code}`
   * The code an attendant typed when the QR could not be scanned; the server reads it
   * leniently and answers 400 / 404 / 409 with a message fit to show.
   */
  getPrepaidPurchaseStatusByRedemptionCode(
    redemptionCode: string
  ): Observable<ApiResponse<PrepaidPurchaseStatus>> {
    const value = redemptionCode?.trim();
    if (!value) {
      return throwError(() => new Error('redemption code is required'));
    }
    const endpoint = `${PrepaidWashBookService.PURCHASES_ENDPOINT}/status/by-code`;
    return this.apiService.get<PrepaidPurchaseStatus>(endpoint, { redemption_code: value }).pipe(
      catchError(err => {
        this.logger.error(
          'Failed to fetch prepaid purchase status by redemption code',
          err,
          'PrepaidWashBookService'
        );
        return throwError(() => err);
      })
    );
  }

  /** The status a typed redemption code names, unwrapped like a scan result (CWS-1908). */
  resolvePrepaidPurchaseStatusFromCode(redemptionCode: string): Observable<PrepaidPurchaseStatus> {
    return this.getPrepaidPurchaseStatusByRedemptionCode(redemptionCode).pipe(
      map(response =>
        this.extractPrepaidPurchaseStatus(response, 'No prepaid book found for this code.')
      )
    );
  }

  /** Resolve a scanned QR payload to full prepaid purchase status. */
  resolvePrepaidPurchaseStatusFromScan(
    payload: PrepaidRedemptionScanPayload
  ): Observable<PrepaidPurchaseStatus> {
    const token = payload.redemptionToken?.trim();
    if (token) {
      return this.getPrepaidPurchaseStatusByRedemptionToken(token).pipe(
        map(response =>
          this.extractPrepaidPurchaseStatus(response, 'Prepaid purchase not found for token.')
        )
      );
    }

    const directId = payload.prepaidPurchaseId?.trim();
    if (directId) {
      return this.getPrepaidPurchaseStatus(directId).pipe(
        map(response => this.extractPrepaidPurchaseStatus(response, 'Prepaid purchase not found.'))
      );
    }

    return throwError(() => new Error('Invalid prepaid QR scan payload.'));
  }

  private extractPrepaidPurchaseStatus(
    response: ApiResponse<PrepaidPurchaseStatus>,
    fallbackMessage: string
  ): PrepaidPurchaseStatus {
    if (response.status && response.data) {
      return response.data;
    }
    throw new Error(response.message || fallbackMessage);
  }

  /**
   * `GET package/prepaid-wash-books/purchases/{prepaidPurchaseId}/redemptions`
   */
  getPrepaidPurchaseRedemptions(
    prepaidPurchaseId: string,
    params: PrepaidPurchaseRedemptionsListParams = {}
  ): Observable<PrepaidPurchaseRedemptionsListResult> {
    const id = prepaidPurchaseId?.trim();
    if (!id) {
      return throwError(() => new Error('prepaidPurchaseId is required'));
    }
    const page = params.page ?? 1;
    const limit = params.limit ?? DEFAULT_PREPAID_PURCHASE_REDEMPTIONS_PAGE_LIMIT;
    const endpoint = `${PrepaidWashBookService.PURCHASES_ENDPOINT}/${id}/redemptions`;
    const query: Record<string, string | number> = { page, limit };
    this.logger.debug(
      'Fetching prepaid purchase redemptions',
      { prepaidPurchaseId: id, page, limit },
      'PrepaidWashBookService'
    );
    return this.apiService.get<PrepaidPurchaseRedemption[]>(endpoint, query).pipe(
      map(response => this.mapRedemptionsListResponse(response, page, limit)),
      catchError(err => {
        this.logger.error(
          'Failed to fetch prepaid purchase redemptions',
          err,
          'PrepaidWashBookService'
        );
        return throwError(() => err);
      })
    );
  }

  /**
   * `POST package/prepaid-wash-books/purchases/{prepaidPurchaseId}/redeem`
   */
  redeemPrepaidPurchase(
    prepaidPurchaseId: string,
    payload: RedeemPrepaidPurchasePayload
  ): Observable<ApiResponse<any>> {
    const id = prepaidPurchaseId?.trim();
    if (!id) {
      return throwError(() => new Error('prepaidPurchaseId is required'));
    }
    const endpoint = `${PrepaidWashBookService.PURCHASES_ENDPOINT}/${id}/redeem`;
    this.logger.debug(
      'Redeeming prepaid purchase',
      { prepaidPurchaseId: id, payload },
      'PrepaidWashBookService'
    );
    return this.apiService.post<any>(endpoint, payload).pipe(
      catchError(err => {
        this.logger.error('Failed to redeem prepaid purchase', err, 'PrepaidWashBookService');
        return throwError(() => err);
      })
    );
  }

  /**
   * `GET package/prepaid-wash-books?status=true&page=1&limit=10&search=...&location_id=...`
   */
  getPrepaidWashBooks(
    params: PrepaidWashBooksListParams = {}
  ): Observable<PrepaidWashBooksListResult> {
    const page = params.page ?? 1;
    const limit = params.limit ?? DEFAULT_PREPAID_WASH_BOOKS_PAGE_LIMIT;
    const query: Record<string, string | number | boolean> = { page, limit };
    if (params.status !== undefined) {
      query['status'] = params.status;
    }
    const search = params.search?.trim();
    if (search) {
      query['search'] = search;
    }
    const locationId = params.location_id?.trim();
    if (locationId) {
      query['location_id'] = locationId;
    }

    this.logger.debug('Fetching prepaid wash books', query, 'PrepaidWashBookService');

    return this.apiService.get<PrepaidWashBook[]>(PrepaidWashBookService.LIST_ENDPOINT, query).pipe(
      map(response => this.mapListResponse(response, page, limit)),
      catchError(err => {
        this.logger.error('Failed to fetch prepaid wash books', err, 'PrepaidWashBookService');
        return throwError(() => err);
      })
    );
  }

  private mapListResponse(
    response: ApiResponse<PrepaidWashBook[]> & Partial<PrepaidWashBooksListApiResponse>,
    page: number,
    limit: number
  ): PrepaidWashBooksListResult {
    const raw = response as PrepaidWashBooksListApiResponse;
    const books = Array.isArray(raw.data) ? raw.data : [];
    const pagination = raw.pagination ?? emptyPrepaidWashBooksPagination(page, limit);
    return { books, pagination };
  }

  private mapRedemptionsListResponse(
    response: ApiResponse<PrepaidPurchaseRedemption[]> &
      Partial<PrepaidPurchaseRedemptionsListApiResponse>,
    page: number,
    limit: number
  ): PrepaidPurchaseRedemptionsListResult {
    const raw = response as PrepaidPurchaseRedemptionsListApiResponse;
    const redemptions = Array.isArray(raw.data) ? raw.data : [];
    const pagination = raw.pagination ?? emptyPrepaidWashBooksPagination(page, limit);
    return { redemptions, pagination };
  }
}
